import React, { lazy } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { asyncRouteLoader } from 'utils/asyncRouteLoader';
import RouteAnalytics from './RouteAnalytics';

const ProjectTeams = asyncRouteLoader(
  () => import('containers/ProjectTeams'),
  [
    {
      type: 'reducer',
      reducerKey: 'projectTeams',
      importPromise: () => import('containers/ProjectTeams/reducer'),
    },
  ]
);
const Team = asyncRouteLoader(
  () => import('containers/Team'),
  [
    {
      type: 'reducer',
      reducerKey: 'team',
      importPromise: () => import('containers/Team/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Team/sagas'),
    },
    {
      type: 'reducer',
      reducerKey: 'integrations',
      importPromise: () => import('containers/Integrations/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Integrations/sagas'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Solution/Version/redux/sagas'),
    },
    {
      type: 'reducer',
      reducerKey: 'teamWidgets',
      importPromise: () => import('containers/Team/TeamWidgets/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Team/TeamWidgets/sagas'),
    },
    {
      type: 'reducer',
      reducerKey: 'userManagement',
      importPromise: () => import('containers/UserManagement/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/UserManagement/sagas'),
    },
  ]
);
const TeamDashboard = asyncRouteLoader(
  () => import('containers/Team/TeamDashboard'),
  [
    {
      type: 'saga',
      importPromise: () => import('containers/Team/TeamDashboard/sagas'),
    },
    {
      type: 'reducer',
      reducerKey: 'teamDashboard',
      importPromise: () => import('containers/Team/TeamDashboard/reducers'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/PublicLanding/sagas'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Widgets/sagas'),
    },
  ]
);
const TeamApps = lazy(() => import('containers/Team/TeamApps'));
const TeamMembers = asyncRouteLoader(
  () => import('containers/Team/TeamMembers'),
  [
    {
      type: 'reducer',
      reducerKey: 'teamMembers.users',
      importPromise: () => import('containers/Team/TeamMembers/reducerUsers'),
    },
    {
      type: 'reducer',
      reducerKey: 'teamMembers.groups',
      importPromise: () => import('containers/Team/TeamMembers/reducerGroups'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Team/TeamMembers/sagas'),
    },
  ]
);
const TeamApp = asyncRouteLoader(
  () => import('containers/Team/TeamApp'),
  [
    {
      type: 'reducer',
      reducerKey: 'teamApp',
      importPromise: () => import('containers/Team/TeamApp/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Team/TeamApp/sagas'),
    },
  ]
);
const TeamActions = asyncRouteLoader(
  () => import('containers/Team/TeamActions'),
  [
    {
      type: 'reducer',
      reducerKey: 'teamActions',
      importPromise: () => import('containers/Team/TeamActions/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Team/TeamActions/sagas'),
    },
  ]
);
const EditTeamActions = asyncRouteLoader(
  () => import('containers/Team/EditTeamActions'),
  [
    {
      type: 'reducer',
      reducerKey: 'editTeamActions',
      importPromise: () => import('containers/Team/EditTeamActions/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Team/EditTeamActions/sagas'),
    },
  ]
);
const TeamAssessments = asyncRouteLoader(
  () => import('containers/Team/TeamAssessments'),
  [
    {
      type: 'saga',
      importPromise: () => import('containers/Widgets/sagas'),
    },
  ]
);
const Project = asyncRouteLoader(
  () => import('containers/Project'),
  [
    {
      type: 'reducer',
      reducerKey: 'assessment',
      importPromise: () => import('containers/Project/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Project/sagas'),
    },
    {
      type: 'reducer',
      reducerKey: 'projectView',
      importPromise: () => import('containers/ProjectView/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/ProjectView/sagas'),
    },
    {
      type: 'reducer',
      reducerKey: 'transcript',
      importPromise: () => import('containers/Transcript/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Transcript/sagas'),
    },
    {
      type: 'reducer',
      reducerKey: 'team',
      importPromise: () => import('containers/Team/reducer'),
    },
  ]
);
const ViewDocument = lazy(() => import('containers/ViewDocument'));
const TeamWidgets = lazy(() => import('containers/Team/TeamWidgets'));
const TeamImportAssessments = lazy(
  () => import('containers/Team/TeamImportAssessments')
);
const AppPermissions = asyncRouteLoader(
  () => import('containers/Team/AppPermissions'),
  [
    {
      type: 'reducer',
      reducerKey: 'appPermissions',
      importPromise: () => import('containers/Team/AppPermissions/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Team/AppPermissions/sagas'),
    },
  ]
);
const TeamDetails = asyncRouteLoader(
  () => import('containers/Team/TeamDetails'),
  [
    {
      type: 'reducer',
      reducerKey: 'integrations',
      importPromise: () => import('containers/Integrations/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Integrations/sagas'),
    },
  ]
);
const TeamDelete = lazy(() => import('containers/Team/TeamDelete'));
const LegacyAssessmentLinkHandler = lazy(
  () => import('containers/Team/LegacyAssessmentLinkHandler')
);
const TeamWorkflow = lazy(() => import('containers/Team/TeamWorkflow'));
const Transcript = asyncRouteLoader(
  () => import('containers/Transcript'),
  [
    {
      type: 'reducer',
      reducerKey: 'transcript',
      importPromise: () => import('containers/Transcript/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Transcript/sagas'),
    },
  ]
);

export const projectTeamRoutes = () => (
  <>
    <Route path="teams" element={<ProjectTeams />} />
    <Route path="team/:teamId" element={<Team />}>
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route path="dashboard" element={<TeamDashboard />} />
      <Route path="dashboard/:dashboardID" element={<TeamDashboard />} />
      <Route path="solutions" element={<TeamApps />} />
      <Route path="users" element={<TeamMembers />} />
      <Route path="app/:appID" element={<TeamApp />}>
        <Route index element={<Navigate to="assessments" replace />} />
        <Route
          path="actions"
          element={
            <RouteAnalytics pageName="Team-App-Tasks">
              <TeamActions />
            </RouteAnalytics>
          }
        />
        <Route
          path="edit-action/:actionId"
          element={
            <RouteAnalytics pageName="Team-App-Task-Editor">
              <EditTeamActions />
            </RouteAnalytics>
          }
        />
        <Route
          path="assessments"
          element={
            <RouteAnalytics pageName="Team-App-Session">
              <TeamAssessments />
            </RouteAnalytics>
          }
        />
        <Route path="assessments/:create" element={<TeamAssessments />} />
        <Route
          path="workflow"
          element={
            <RouteAnalytics pageName="Team-App-Workflow">
              <TeamWorkflow />
            </RouteAnalytics>
          }
        />
        <Route
          path="assessment/:assessmentId/transcript"
          element={
            <RouteAnalytics pageName="Team-App-Session-Transcript">
              <Transcript />
            </RouteAnalytics>
          }
        />
        <Route
          path="assessment/:assessmentId"
          element={
            <RouteAnalytics pageName="Team-App-Session">
              <Project />
            </RouteAnalytics>
          }
        />
        <Route
          path="assessment/:assessmentId/:pageUUID"
          element={
            <RouteAnalytics pageName="Team-App-Session">
              <Project />
            </RouteAnalytics>
          }
        />
        <Route
          path="assessment/:assessmentId/document/:documentId"
          element={
            <RouteAnalytics pageName="Team-App-Session-Document">
              <ViewDocument />
            </RouteAnalytics>
          }
        />
        <Route
          path="widgets"
          element={
            <RouteAnalytics pageName="Team-App-Widgets">
              <TeamWidgets />
            </RouteAnalytics>
          }
        />
        <Route
          path="importassessments"
          element={
            <RouteAnalytics pageName="Team-App-Import">
              <TeamImportAssessments />
            </RouteAnalytics>
          }
        />
        <Route
          path="permissions"
          element={
            <RouteAnalytics pageName="Team-App-Permissions">
              <AppPermissions />
            </RouteAnalytics>
          }
        />
      </Route>
      <Route
        path="details"
        element={
          <RouteAnalytics pageName="Team-Details">
            <TeamDetails />
          </RouteAnalytics>
        }
      />
      <Route
        path="delete"
        element={
          <RouteAnalytics pageName="Team-Delete">
            <TeamDelete />
          </RouteAnalytics>
        }
      />
      {/*
        Before multi-app team, routes did not have `app/:appID` in them
        The code below resolves appID via assessmentID and redirect user to
        a correct page
      */}
      <Route
        path="assessment/:assessmentID*"
        element={<LegacyAssessmentLinkHandler />}
      />
    </Route>
  </>
);
